import {
	BASE_PRECISION,
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	calculateEstimatedEntryPriceWithL2,
	FOUR,
	MarketStatus,
	MarketType,
	PositionDirection,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	SpotMarketAccount,
	SpotMarketConfig,
	ZERO,
} from '@drift-labs/sdk';
import { ENUM_UTILS, matchEnum, MultiSwitch, UIOrderType } from '@drift/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Env, {
	marketSizeIncs,
} from 'src/environmentVariables/EnvironmentVariables';
import useAccountData from 'src/hooks/useAccountData';
import useAccountExists from 'src/hooks/useAccountExists';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import useCurrentMarketMarkPrice from 'src/hooks/useCurrentMarketMarkPrice';
import useDriftActions from 'src/hooks/useDriftActions';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { useTradeformPriceImpact } from 'src/hooks/usePriceImpact';
import useUserAccountIsReady from 'src/hooks/useUserAccountIsReady';
import useDriftStore, { DriftStore } from 'src/stores/DriftStore/useDriftStore';
import NumLib from 'src/utils/NumLib';
import UI_UTILS from 'src/utils/uiUtils';
import useCurrentPerpMarketAccount from '../../hooks/useCurrentPerpMarketAccount';
import useWalletIsConnected from '../../hooks/useWalletIsConnected';
import Button from '../Button';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import {
	MAX_NUMBER_OF_PERP_POSITIONS,
	SUPER_STAKE_ACCOUNT_NAMES,
} from 'src/constants/constants';
import useUserWillExceedMaxPerpsPositions from 'src/hooks/useUserWillExceedMaxPerpsPositions';
import useSafePush from 'src/hooks/useSafePush';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import usePriceForOracleOffsetOrder from 'src/hooks/usePriceForOracleOffsetOrders';
import { TradeFormMessageType, TradeFormState } from 'src/@types/types';
import {
	getMessageInfoForOrderViolation,
	getPriceBoxHeading,
	getShouldShowSecondaryPriceBox,
	MessageInfo,
} from 'src/utils/trade';
import useIsLiteMode from 'src/hooks/useIsLiteMode';
import useL2StateForMarket from 'src/hooks/useL2StateForMarket';

const DepositButton = () => {
	const actions = useDriftActions();
	const wallet = useDriftStore((s) => s.wallet);
	const safePush = useSafePush();

	const clickAction = useCallback(() => {
		if (
			(wallet?.isMagicAuth || wallet?.isMetamask) &&
			wallet?.currentSolBalance.eqZero()
		) {
			safePush('/onboarding/fund-your-account?source=tradeform');
		} else {
			actions.showDepositModal(0, undefined, true);
		}
	}, [actions, wallet]);

	return (
		<Button.Secondary size="MEDIUM" onClick={clickAction}>
			Deposit
		</Button.Secondary>
	);
};

const getMessageInfoForTradeformState = (
	isSpot: boolean,
	currentOrderType: UIOrderType,
	maxQuoteAvailable: number,
	connected: boolean,
	accountExists: boolean,
	tradeForm: DriftStore['tradeForm'],
	priceBoxValue: number,
	currentMarketPrice: number,
	hasCurrentPosition: boolean,
	isGeoblocked: boolean,
	minTradeSizeForMarket: number,
	marketSymbol: string,
	entryPrice: BigNum,
	marketType: MarketType,
	openAddAccountModal: () => void,
	isMaxPerpPositions: boolean,
	oraclePrice: BigNum,
	currentPositionDirection?: 'long' | 'short',
	currentPositionSize?: BigNum,
	basePrecisionToUse?: BN,
	userHasLpShares?: boolean,
	accountName?: string,
	currentPositionIsZero?: boolean
): MessageInfo => {
	const baseSize = BigNum.fromPrint(
		tradeForm.baseSizeStringValue,
		basePrecisionToUse || BASE_PRECISION_EXP
	);

	const tradeSizeIsTooSmall =
		!baseSize || baseSize.eqZero()
			? false
			: minTradeSizeForMarket !== undefined &&
			  baseSize.toNum() < minTradeSizeForMarket;

	// Trade Size Too Small Case
	if (tradeSizeIsTooSmall) {
		return {
			messageTitle: '',
			shouldShowMessage: true,
			message: `Order size must be greater than ${minTradeSizeForMarket} ${marketSymbol}`,
			messageType: TradeFormMessageType.error,
			skipMessageInConfirmationModal: true,
			disableTradeButton: true,
		};
	}

	if (isMaxPerpPositions) {
		return {
			messageType: TradeFormMessageType.error,
			shouldShowMessage: true,
			messageTitle: '',
			message: `You have reached the maximum number of Perp Positions (${MAX_NUMBER_OF_PERP_POSITIONS}) for a single user account. You will need to remove a position from another market before you can add liquidity to this pool.`,
			skipMessageInConfirmationModal: false,
			disableTradeButton: true,
		};
	}

	if (
		baseSize &&
		!baseSize.eqZero() &&
		(userHasLpShares || UI_UTILS.accountNameIsLp(accountName))
	) {
		return {
			messageTitle: ``,
			shouldShowMessage: true,
			message: (
				<div className="flex flex-col gap-1">
					<div>
						To easily track your P&amp;L, keep trading activity and LPing in
						separate subaccounts.
					</div>
					<Button.Secondary
						size="SMALL"
						onClick={openAddAccountModal}
						className="self-start"
					>
						Add subaccount
					</Button.Secondary>
				</div>
			),
			messageType: TradeFormMessageType.warn,
			skipMessageInConfirmationModal: false,
			disableTradeButton: false,
		};
	}

	if (SUPER_STAKE_ACCOUNT_NAMES.includes(accountName)) {
		return {
			messageTitle: ``,
			shouldShowMessage: true,
			message: (
				<div className="flex flex-col gap-1">
					<div>
						To easily track your P&amp;L, keep trading activity and Superstaking
						in separate subaccounts.
					</div>
					<Button.Secondary
						size="SMALL"
						onClick={openAddAccountModal}
						className="self-start"
					>
						Add subaccount
					</Button.Secondary>
				</div>
			),
			messageType: TradeFormMessageType.warn,
			skipMessageInConfirmationModal: false,
			disableTradeButton: false,
		};
	}

	if (isGeoblocked) {
		return {
			shouldShowMessage: true,
			messageTitle: 'Geoblocked',
			message: `You are not allowed to use Drift from a restricted territory. Only reduce only orders are allowed.`,
			messageType: TradeFormMessageType.error,
			skipMessageInConfirmationModal: false,
		};
	}

	if (connected && accountExists === false) {
		return {
			messageType: TradeFormMessageType.warn,
			shouldShowMessage: true,
			messageTitle: 'Deposit assets to start trading',
			message: <DepositButton />,
			skipMessageInConfirmationModal: false,
		};
	}

	const baseCase = {
		shouldShowMessage: false,
		messageTitle: '',
		message: '',
		messageType: TradeFormMessageType.info,
		skipMessageInConfirmationModal: true,
	};

	const isBuySide = tradeForm.side === 'buy';

	const secondaryPriceBoxValue = Number(tradeForm.secondaryPriceBoxStringValue);

	if (tradeForm.bracketOrders) {
		const comparePrice =
			tradeForm.orderType === 'market'
				? entryPrice
				: BigNum.fromPrint(tradeForm.priceBoxStringValue, PRICE_PRECISION_EXP);

		if (tradeForm.bracketOrders.takeProfit) {
			if (
				tradeForm.bracketOrders.takeProfit.price.gt(comparePrice) &&
				tradeForm.side === 'sell'
			) {
				return {
					messageTitle: `Take Profit Bracket Order trigger oracle price is invalid`,
					shouldShowMessage: true,
					message: `Take Profit trigger oracle price must be below entry price`,
					messageType: TradeFormMessageType.error,
					skipMessageInConfirmationModal: false,
					disableTradeButton: true,
				};
			}

			if (
				tradeForm.bracketOrders.takeProfit.price.lt(comparePrice) &&
				tradeForm.side === 'buy'
			) {
				return {
					messageTitle: `Take Profit Bracket Order trigger oracle price is invalid`,
					shouldShowMessage: true,
					message: `Take Profit trigger oracle price must be above entry price`,
					messageType: TradeFormMessageType.error,
					skipMessageInConfirmationModal: false,
					disableTradeButton: true,
				};
			}
		}

		if (tradeForm.bracketOrders.stopLoss) {
			if (
				tradeForm.bracketOrders.stopLoss.price.gt(comparePrice) &&
				tradeForm.side === 'buy'
			) {
				return {
					messageTitle: `Stop Loss Bracket Order trigger oracle price is invalid`,
					shouldShowMessage: true,
					message: `Stop loss trigger oracle price must be below entry price`,
					messageType: TradeFormMessageType.error,
					skipMessageInConfirmationModal: false,
					disableTradeButton: true,
				};
			}

			if (
				tradeForm.bracketOrders.stopLoss.price.lt(comparePrice) &&
				tradeForm.side === 'sell'
			) {
				return {
					messageTitle: `Stop Loss Bracket Order trigger oracle price is invalid`,
					shouldShowMessage: true,
					message: `Stop Loss trigger oracle price must be above entry price`,
					messageType: TradeFormMessageType.error,
					skipMessageInConfirmationModal: false,
					disableTradeButton: true,
				};
			}
		}
	}

	// Check errors which apply to market orders only
	if (currentOrderType === 'market') {
		// None of the following error cases apply to market orders
		return baseCase;
	}

	// Case where reduce-only, and has a current position, but the current trade would make current position larger
	// We allow this case for users to preemptively set up their exits before getting into a position
	if (
		tradeForm.reduceOnly &&
		hasCurrentPosition &&
		currentPositionDirection &&
		(currentPositionDirection === 'long' ? 'buy' : 'sell') === tradeForm.side
	) {
		const oppositeOrderSide =
			currentPositionDirection === 'long'
				? ENUM_UTILS.match(marketType, MarketType.SPOT)
					? 'Sell'
					: 'Short'
				: ENUM_UTILS.match(marketType, MarketType.SPOT)
				? 'Buy'
				: 'Long';

		return {
			messageTitle: `Reduce Only Selected`,
			shouldShowMessage: true,
			message: `This order can only fill if you move into a "${oppositeOrderSide}" position. This feature is designed so that you can preemptively set up your exits before getting into a position. If this is not your intention then you may be putting through the wrong order.
			`,
			messageType: TradeFormMessageType.info,
			skipMessageInConfirmationModal: false,
			disableTradeButton: false,
		};
	}

	// Trade size too large case
	if (
		tradeForm.reduceOnly &&
		currentPositionSize &&
		!currentPositionSize.eq(ZERO) &&
		baseSize.gt(currentPositionSize.abs())
	) {
		return {
			messageTitle: `Trade size too large`,
			shouldShowMessage: true,
			message: `You can't open an order larger than your current position while reduce-only is enabled`,
			messageType: TradeFormMessageType.error,
			skipMessageInConfirmationModal: false,
			disableTradeButton: true,
		};
	}

	// No trade amount case
	if ((!baseSize || baseSize.eqZero()) && maxQuoteAvailable > 0) {
		return {
			shouldShowMessage: true,
			message: `Enter a trade amount`,
			messageTitle: '',
			messageType: TradeFormMessageType.warn,
			skipMessageInConfirmationModal: false,
			disableTradeButton: false,
		};
	}

	const orderViolationMessage = getMessageInfoForOrderViolation(
		currentOrderType,
		isSpot,
		priceBoxValue,
		currentMarketPrice,
		tradeForm.postOnly,
		secondaryPriceBoxValue,
		isBuySide ? 'long' : 'short',
		currentPositionDirection,
		oraclePrice?.toNum(),
		currentPositionIsZero
	);

	if (orderViolationMessage) return orderViolationMessage;

	return baseCase;
};

const marketSwitchPriceBoxUpdate = (
	defaultPrice: BigNum,
	currentOrderType: UIOrderType,
	direction: 'long' | 'short',
	currentPositionDirection: 'long' | 'short',
	slippageTolerance: number,
	priceBoxUpdate: (string) => void,
	secondaryPriceBoxUpdate: (string) => void,
	tickSize?: BigNum,
	savedLimitPrice?: BigNum
) => {
	if (!defaultPrice && !savedLimitPrice) return;

	const limitPriceToUse = savedLimitPrice ?? defaultPrice;

	const priceDefaultMod = 1 / 100; // 1% default offset for stop / take profit / limit
	if (currentOrderType == 'limit') {
		priceBoxUpdate(limitPriceToUse.toTradePrecision());
	} else if (currentOrderType == 'oracleLimit') {
		priceBoxUpdate(
			!tickSize
				? ''
				: direction === 'short'
				? tickSize.prettyPrint()
				: tickSize.neg().prettyPrint()
		);
	} else {
		const useHigherPrice =
			(currentPositionDirection === 'short' &&
				(currentOrderType == 'stopMarket' ||
					currentOrderType == 'stopLimit')) ||
			(currentPositionDirection === 'long' &&
				(currentOrderType == 'takeProfitMarket' ||
					currentOrderType == 'takeProfitLimit'));

		const isAdvancedLimit =
			currentOrderType == 'stopLimit' || currentOrderType == 'takeProfitLimit';

		let priceBoxVal = Number(defaultPrice.toTradePrecision());
		let secondaryBoxVal = Number(defaultPrice.toTradePrecision());

		if (useHigherPrice) {
			priceBoxVal = NumLib.formatNum.toTradePrecision(
				priceBoxVal * (1 + priceDefaultMod)
			);

			if (currentOrderType == 'stopLimit') {
				secondaryBoxVal = NumLib.formatNum.toTradePrecision(
					priceBoxVal * (1 + slippageTolerance)
				);
			}

			if (currentOrderType == 'takeProfitLimit') {
				secondaryBoxVal = NumLib.formatNum.toTradePrecision(
					priceBoxVal * (1 + slippageTolerance)
				);
			}
		} else {
			if (currentOrderType === 'stopLimit') {
				secondaryBoxVal = NumLib.formatNum.toTradePrecision(
					priceBoxVal * (1 - slippageTolerance)
				);
			}

			if (currentOrderType === 'takeProfitLimit') {
				secondaryBoxVal = NumLib.formatNum.toTradePrecision(
					priceBoxVal * (1 - slippageTolerance)
				);
			}
		}

		priceBoxUpdate(priceBoxVal ? priceBoxVal.toString() : '');

		if (isAdvancedLimit)
			secondaryPriceBoxUpdate(
				secondaryBoxVal ? secondaryBoxVal.toString() : ''
			);
	}
};

/**
 * A hook which does all of the logic to determine how the spot tradeform should behave based on its state
 * @returns
 */
export const usePerpTradeFormStateEngine = (): TradeFormState => {
	// Generic App Hooks
	const setState = useDriftStore((s) => s.set);
	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);
	const tradeForm = useDriftStore((s) => s.tradeForm);
	const currentOrderType = useDriftStore((s) => s.tradeForm.orderType);
	const driftClientIsReady = useDriftClientIsReady();
	const connected = useWalletIsConnected();
	const currentAccount = useAccountData();
	const accountExists = useAccountExists();
	const userAccountIsReady = useUserAccountIsReady();
	const [currentSettings] = useCurrentSettings();
	const {
		slippageTolerance: globalSlippageTolerance,
		allowInfSlippage: globalAllowInfSlippage,
	} = currentSettings;
	const currentPerpMarket = useCurrentPerpMarketAccount();
	const isLiteMode = useIsLiteMode();

	// React Refs
	const showMaxButtonMultiSwitch = useRef(new MultiSwitch('on'));
	const showMaxLeverageSliderMultiSwitch = useRef(new MultiSwitch('on'));
	const showReducePositionSliderMultiSwitch = useRef(new MultiSwitch('off'));

	// Selected Market
	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const selectedMarketIndex = selectedMarket.market.marketIndex;

	// L2 state for est entry price to calculate max base size
	const l2State = useL2StateForMarket(
		selectedMarket?.marketIndex,
		selectedMarket?.marketType
	);

	const dlobIsLoaded =
		!!l2State && !!(l2State?.asks?.length || l2State?.bids?.length);

	// Trade Form
	const quoteSize = Number(tradeForm.quoteSizeStringValue);
	const priceBoxValue = Number(tradeForm.priceBoxStringValue);
	const tradeFormSlippageTolerance = Math.max(
		0,
		tradeForm.slippageTolerance / 100
	);

	// Oracle and Market Prices
	const oraclePrice = usePriceForOracleOffsetOrder(selectedMarket.marketId);
	const currentMarketPriceBigNum = useCurrentMarketMarkPrice();
	const currentMarketPrice = parseFloat(currentMarketPriceBigNum.print());

	const { entryPrice: entryPriceBn, showPriceEstimateOracleDivergenceWarning } =
		useTradeformPriceImpact();

	const userWillExceedMaxPerpsPositions = useUserWillExceedMaxPerpsPositions(
		selectedMarket?.market.marketIndex
	);
	const userHasPositionInMarket =
		currentAccount?.openPerpPositions
			?.map((val) => val.marketIndex.toString())
			.includes(selectedMarketIndex?.toString()) ?? false;
	const userHasLpShares = currentAccount?.marginInfo?.quoteInLpOrders?.gt(ZERO);

	const savedPriceInfo = tradeForm.savedLimitPrice;
	const savedLimitPriceBigNum =
		savedPriceInfo && selectedMarket.marketId.equals(savedPriceInfo?.market)
			? BigNum.fromPrint(savedPriceInfo.price.toString(), PRICE_PRECISION_EXP)
			: undefined;

	const freeCollateral = NumLib.formatBn.fromQuote(
		currentAccount?.marginInfo?.freeCollateral ?? new BN(0)
	);

	const tickSize = currentPerpMarket?.amm?.orderTickSize;

	const currentPosition =
		userAccountIsReady && currentAccount
			? currentAccount.client
					?.getUserAccount()
					.perpPositions.find(
						(position) => position.marketIndex === selectedMarketIndex
					)
			: undefined;

	const currentPositionIsZero =
		!currentPosition || currentPosition.baseAssetAmount.eq(ZERO);
	const currentPositionDirection = currentPosition
		? currentPosition.baseAssetAmount.isNeg()
			? 'short'
			: 'long'
		: // for logical purposes position is "long" if position is zero
		  'long';

	const marketInSettlement = matchEnum(
		currentPerpMarket?.status,
		MarketStatus.SETTLEMENT
	);

	// Show Reduce Only
	const showReduceOnly = (() => {
		const selectedDirection = tradeForm.side == 'buy' ? 'long' : 'short';
		switch (currentOrderType) {
			case 'market':
				return (
					userHasPositionInMarket &&
					currentPositionDirection != selectedDirection
				);
			default:
				return true;
		}
	})();

	// Price Box Heading
	const priceBoxHeading = getPriceBoxHeading(currentOrderType);

	// Price Box Enabled
	const priceBoxEnabled = (() => {
		switch (currentOrderType) {
			case 'market':
				return false;
			case 'oracle':
				return false;
			default:
				return true;
		}
	})();

	// Show Slippage Tolerance
	const showSlippageTolerance = (() => {
		switch (currentOrderType) {
			case 'market':
				return true;
			default:
				return false;
		}
	})();

	// Show Secondary Price Box
	const showSecondaryPriceBox =
		getShouldShowSecondaryPriceBox(currentOrderType);

	// Show Post Only
	const showPostOnly = useMemo(
		() => currentOrderType === 'limit' || currentOrderType === 'oracleLimit',
		[currentOrderType]
	);

	/**
	 * Handle keeping reduce-only in sync
	 * 
	 * - On Market Orders, 
	 * 	- if ShowReduceOnly is true, then it should be switched to match the user's preference
	 * 	- if showReduceOnly is false, then it should be switched off
	 * - On Limit Orders,
	 * 	- switch should be set to match the user's preference
	 * 	- if switching reduce only on, then make sure we turn postOnly off
	 * - On STOP and TAKEPROFIT orders reduce only should default to true, without afftecting the user's preferences
	 * 		
		if (currentOrderType === 'limit' || currentOrderType === 'market') {
			setReduceOnly(reduceOnlyPreference);
			if (tradeForm.postOnly && reduceOnlyPreference) {
				setState((s) => {
					s.tradeForm.postOnly = false;
				});
			}
		}
	 */

	// React useEffects

	// When order type changes, default reduce-only to true for stop-loss or take-profit orders
	useEffect(() => {
		const defaultReduceOnlyOrderTypes: UIOrderType[] = [
			'stopLimit',
			'stopMarket',
			'takeProfitLimit',
			'takeProfitMarket',
		];

		if (defaultReduceOnlyOrderTypes.includes(currentOrderType)) {
			setReduceOnly(true);

			// Switch form to opposite side of current position if current position > 0
			if (
				currentPosition &&
				currentPositionDirection &&
				currentPosition.baseAssetAmount.abs().gt(ZERO)
			) {
				if (currentPositionDirection === 'long' && tradeForm.side === 'buy') {
					setState((s) => {
						s.tradeForm.side = 'sell';
					});
				} else if (
					currentPositionDirection === 'short' &&
					tradeForm.side === 'sell'
				) {
					setState((s) => {
						s.tradeForm.side = 'buy';
					});
				}
			}

			return;
		}

		if (!showReduceOnly) {
			setReduceOnly(false);
		}

		// dont think this is needed? was there a reason we flipped off post only if reduce only was on?
		// if (currentOrderType === 'limit') {
		// 	setReduceOnly(tradeForm.preferReduceOnly);
		// 	if (tradeForm.postOnly && tradeForm.preferReduceOnly) {
		// 		setState((s) => {
		// 			s.tradeForm.postOnly = false;
		// 		});
		// 	}
		// 	return;
		// }
	}, [
		currentOrderType,
		currentPositionDirection,
		currentPosition,
		selectedMarket,
		showReduceOnly,
	]);

	// Handle hiding max button based on reduce-only state
	useEffect(() => {
		let shouldShowMaxButtonValue = true;
		const tradeFormDirection = tradeForm.side === 'buy' ? 'long' : 'short';

		if (tradeForm.reduceOnly) {
			// If No Position, hide max button
			if (!currentPosition || currentPosition?.baseAssetAmount?.eq(ZERO)) {
				shouldShowMaxButtonValue = false;
			}

			// If same side as position and reduce-only, hide max button
			if (currentPosition && currentPositionDirection === tradeFormDirection) {
				shouldShowMaxButtonValue = false;
			}
		}

		const SWITCH_KEY = 'reduce_only_control';
		if (shouldShowMaxButtonValue) {
			showMaxButtonMultiSwitch.current.switchOn(SWITCH_KEY);
		} else {
			showMaxButtonMultiSwitch.current.switchOff(SWITCH_KEY);
		}

		if (tradeForm.reduceOnly && shouldShowMaxButtonValue) {
			showReducePositionSliderMultiSwitch.current.switchOn(SWITCH_KEY);
		} else {
			showReducePositionSliderMultiSwitch.current.switchOff(SWITCH_KEY);
		}

		if (tradeForm.reduceOnly) {
			showMaxLeverageSliderMultiSwitch.current.switchOff(SWITCH_KEY);
		} else {
			showMaxLeverageSliderMultiSwitch.current.switchOn(SWITCH_KEY);
		}
	}, [
		currentOrderType,
		currentPositionDirection,
		!!currentPosition,
		selectedMarket,
		tradeForm.side,
		tradeForm.reduceOnly,
	]);

	// Reset price box when changing markets
	useEffect(() => {
		if (!driftClientIsReady) return;
		if (currentOrderType === 'market') {
			setPriceBoxValue('');
			setSecondaryPriceBoxValue('');
		}
	}, [selectedMarket, driftClientIsReady, currentPositionDirection]);

	// Update main price box when changing markets for a non-Market Order
	useEffect(() => {
		if (!driftClientIsReady) return;

		if (currentOrderType !== 'market') {
			if (
				(currentMarketPriceBigNum && !currentMarketPriceBigNum.eqZero()) ||
				savedLimitPriceBigNum
			) {
				marketSwitchPriceBoxUpdate(
					currentMarketPriceBigNum,
					currentOrderType,
					tradeForm.side === 'buy' ? 'long' : 'short',
					currentPositionDirection,
					tradeFormSlippageTolerance,
					setPriceBoxValue,
					setSecondaryPriceBoxValue,
					tickSize ? BigNum.from(tickSize, PRICE_PRECISION_EXP) : undefined,
					savedLimitPriceBigNum
				);
			} else {
				setPriceBoxValue('');
				setSecondaryPriceBoxValue('');
			}
		}
	}, [
		selectedMarketIndex,
		driftClientIsReady,
		currentOrderType,
		currentPositionDirection,
		tradeForm.side,
		savedLimitPriceBigNum?.toNum(),
		currentMarketPriceBigNum?.eqZero(),
		tickSize?.toNumber(),
	]);

	// Show Leverage Slider based on order type
	useEffect(() => {
		const SWITCH_KEY = 'order_type_control';

		switch (currentOrderType) {
			case 'stopMarket':
			case 'takeProfitMarket':
			case 'stopLimit':
			case 'oracleLimit':
				setState((s) => {
					s.tradeForm.baseSizeStringValue = '';
				});
				showMaxLeverageSliderMultiSwitch.current.switchOff(SWITCH_KEY);
				return;
			case 'takeProfitLimit':
				showMaxLeverageSliderMultiSwitch.current.switchOff(SWITCH_KEY);
				return;
			default:
				if (tradeForm.reduceOnly) {
					showMaxLeverageSliderMultiSwitch.current.switchOff(SWITCH_KEY);
					return;
				}
				showMaxLeverageSliderMultiSwitch.current.switchOn(SWITCH_KEY);
				return;
		}
	}, [currentOrderType, tradeForm.reduceOnly]);

	const [maxQuoteAvailable, setMaxQuoteAmountAvailable] = useState(0);
	const [maxBaseAvailable, setMaxBaseAmountAvailable] = useState(0);

	const [maxLeverageAvailable, setMaxLeverageAvailable] = useState(
		Env.defaultMaxMarketLeverage
	);

	/* Keep tradeform slippage tolerance synced if updated from settings */
	useEffect(() => {
		setState((s) => {
			s.tradeForm.slippageTolerance =
				globalSlippageTolerance === 'inf'
					? undefined
					: Number(globalSlippageTolerance);
			s.tradeForm.allowInfSlippage = globalAllowInfSlippage;
		});
	}, [globalSlippageTolerance, globalAllowInfSlippage]);

	// Maintain max allowed quote state
	useEffect(() => {
		if (
			!userAccountIsReady ||
			!driftClientIsReady ||
			!currentAccount ||
			!currentAccount?.client?.isSubscribed
		) {
			setMaxQuoteAmountAvailable(0);
			setMaxBaseAmountAvailable(0);
			return;
		}

		const maxQuoteAllowable = currentAccount.client.getMaxTradeSizeUSDCForPerp(
			selectedMarketIndex,
			tradeForm.side === 'buy'
				? PositionDirection.LONG
				: PositionDirection.SHORT
		);

		setMaxQuoteAmountAvailable(
			BigNum.from(maxQuoteAllowable, QUOTE_PRECISION_EXP).toNum()
		);

		if (tradeForm.orderType === 'market' && dlobIsLoaded && l2State) {
			// We have to actually GET a separate entry price here, assuming the max quote size
			// Otherwise this will not factor in price impact and will be incorrect
			const { entryPrice } = calculateEstimatedEntryPriceWithL2(
				'quote',
				maxQuoteAllowable,
				tradeForm.side === 'buy'
					? PositionDirection.LONG
					: PositionDirection.SHORT,
				BASE_PRECISION,
				l2State
			);

			const maxBase =
				entryPrice && entryPrice.gt(ZERO)
					? BigNum.from(maxQuoteAllowable, QUOTE_PRECISION_EXP)
							.scale(1_000_000, entryPrice?.toNumber())
							.toNum()
					: 0;

			setMaxBaseAmountAvailable(maxBase);
		} else if (oraclePrice && oraclePrice.gt(ZERO)) {
			setMaxBaseAmountAvailable(
				BigNum.from(maxQuoteAllowable, QUOTE_PRECISION_EXP)
					.scale(100_000, oraclePrice.toNum() * 100_000)
					.toNum()
			);
		}

		return;
	}, [
		freeCollateral,
		maxLeverageAvailable,
		//currentPosition,
		selectedMarketIndex,
		tradeForm.side,
		tradeForm.orderType,
		currentMarketPrice,
		userAccountIsReady,
		driftClientIsReady,
		connected,
		oraclePrice?.toString(),
		dlobIsLoaded,
		l2State,
	]);

	// Maintain max allowed leverage state
	useEffect(() => {
		if (!userAccountIsReady || !driftClientIsReady || !connected) {
			setMaxLeverageAvailable(Env.defaultMaxMarketLeverage);
			return;
		}

		if (freeCollateral === 0) return;

		try {
			const newMaxLeverage = BigNum.from(
				currentAccount.client.getMaxLeverageForPerp(selectedMarketIndex),
				FOUR
			).toNum();

			setMaxLeverageAvailable(newMaxLeverage);
		} catch (e) {
			// little race condition here after wallet disconnect so wrap in try/catch
		}

		return;
	}, [
		selectedMarketIndex,
		currentAccount,
		maxQuoteAvailable,
		freeCollateral,
		userAccountIsReady,
		driftClientIsReady,
		connected,
	]);

	// reset some settings when switching to lite mode
	useEffect(() => {
		setState((s) => {
			s.tradeForm.reduceOnly = false;
			s.tradeForm.orderType = 'market';
		});
	}, [isLiteMode]);

	const minTradeSizeForMarket = currentPerpMarket
		? BigNum.from(
				currentPerpMarket.amm.minOrderSize,
				BASE_PRECISION_EXP
		  ).toNum()
		: undefined;

	const openAddAccountModal = () => {
		setState((s) => {
			s.modals.showNewSubaccountModal = true;
		});
	};

	const setReduceOnly = (newVal: boolean) => {
		setState((s) => {
			s.tradeForm.reduceOnly = newVal;
		});
	};

	const setPriceBoxValue = (newValue: string) => {
		setState((s) => {
			s.tradeForm.priceBoxStringValue = newValue;
		});
	};

	const setSecondaryPriceBoxValue = (newValue: string) => {
		setState((s) => {
			s.tradeForm.secondaryPriceBoxStringValue = newValue;
		});
	};

	const {
		messageType,
		shouldShowMessage,
		message,
		skipMessageInConfirmationModal,
		messageLink,
		messageLinkDescription,
		messageTitle,
		disableTradeButton,
		messageId,
	} = getMessageInfoForTradeformState(
		false,
		currentOrderType,
		maxQuoteAvailable,
		connected,
		accountExists,
		tradeForm,
		priceBoxValue,
		currentMarketPrice,
		userHasPositionInMarket,
		isGeoblocked,
		minTradeSizeForMarket,
		selectedMarket.baseAssetSymbol(),
		BigNum.from(entryPriceBn, PRICE_PRECISION_EXP),
		MarketType.PERP,
		openAddAccountModal,
		userWillExceedMaxPerpsPositions,
		oraclePrice ? oraclePrice : undefined,
		currentPositionDirection,
		undefined,
		undefined,
		userHasLpShares,
		currentAccount?.name,
		currentPositionIsZero
	);

	const tradeButtonIsDisabled =
		UI_UTILS.valueIsBelowStepSize(
			tradeForm.baseSizeStringValue,
			tradeForm.stepSize
		) ||
		marketInSettlement ||
		disableTradeButton ||
		!tradeForm.baseSizeStringValue ||
		!connected ||
		!quoteSize;

	return {
		orderType: currentOrderType,
		priceBoxHeading,
		priceBoxEnabled,
		showSlippageTolerance,
		shouldShowMessage,
		messageType,
		message,
		skipMessageInConfirmationModal,
		showSecondaryPriceBox,
		showReduceOnly,
		showPostOnly,
		tradeButtonIsDisabled,
		currentPosition,
		currentMarketPrice,
		messageLink,
		messageLinkDescription,
		messageTitle,
		maxQuoteAvailable,
		maxBaseAvailable,
		maxLeverageAvailable,
		messageId,
		marketInSettlement,
		showMaxButton: showMaxButtonMultiSwitch.current.isOn,
		showLeverageSlider: showMaxLeverageSliderMultiSwitch.current.isOn,
		showReducePositionSlider: showReducePositionSliderMultiSwitch.current.isOn,
		showPriceEstimateOracleDivergenceWarning:
			showPriceEstimateOracleDivergenceWarning && currentOrderType === 'market',
	};
};

/**
 * A hook which does all of the logic to determine how the spot tradeform should behave based on its state
 * @returns
 */
export const useSpotTradeFormStateEngine = (): TradeFormState => {
	// Generic App Hooks
	const currentAccount = useAccountData();
	const setState = useDriftStore((s) => s.set);
	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);
	const tradeForm = useDriftStore((s) => s.tradeForm);
	const currentOrderType = useDriftStore((s) => s.tradeForm.orderType);
	const connected = useWalletIsConnected();
	const userAccountIsReady = useUserAccountIsReady();
	const driftClientIsReady = useDriftClientIsReady();
	const accountExists = useAccountExists();
	const marketInfoStore = useMarketsInfoStore();

	// Hook states
	const [maxQuoteAvailable, setMaxQuoteAmountAvailable] = useState(0);
	const [maxBaseAvailable, setMaxBaseAmountAvailable] = useState(0);
	const [maxLeverageAvailable, setMaxLeverageAvailable] = useState(
		Env.defaultMaxMarketLeverage
	);

	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const selectedMarketIndex = selectedMarket.market.marketIndex;
	const spotMarketAccount = marketInfoStore?.getMarketInfoByIndexAndType(
		selectedMarketIndex,
		MarketType.SPOT
	)?.account as SpotMarketAccount;
	const tickSize = spotMarketAccount?.orderTickSize;

	const marketPriceBigNum = useMarkPrice();
	const marketPrice = parseFloat(marketPriceBigNum.toTradePrecision());

	const savedPriceInfo = tradeForm.savedLimitPrice;
	const savedLimitPrice =
		savedPriceInfo && selectedMarket.marketId.equals(savedPriceInfo?.market)
			? BigNum.fromPrint(savedPriceInfo.price.toString(), PRICE_PRECISION_EXP)
			: undefined;

	const basePrecisionToUse = (selectedMarket.market as SpotMarketConfig)
		.precisionExp;

	const currentPosition = useAccountTargetSpotBalance(
		selectedMarket.market as SpotMarketConfig,
		currentAccount?.userKey ?? ''
	);

	const oraclePrice = usePriceForOracleOffsetOrder(selectedMarket.marketId);

	const [currentSettings] = useCurrentSettings();
	const {
		slippageTolerance: globalSlippageTolerance,
		allowInfSlippage: globalAllowInfSlippage,
	} = currentSettings;

	const userHasMarginEnabled = currentAccount?.marginEnabled ?? false;

	const { entryPrice: entryPriceBn, showPriceEstimateOracleDivergenceWarning } =
		useTradeformPriceImpact();

	const currentMarketPriceBigNum = useCurrentMarketMarkPrice();
	const currentMarketPrice = parseFloat(currentMarketPriceBigNum.print());

	const userHasPositionInMarket = !currentPosition.netBaseBalance.eqZero();
	const userHasLpShares = currentAccount?.marginInfo?.quoteInLpOrders?.gt(ZERO);

	const slippageTolerance = Math.max(
		0,
		useDriftStore((s) => s.tradeForm.slippageTolerance) / 100
	);

	const quoteSize = Number(tradeForm.quoteSizeStringValue);
	const priceBoxValue = Number(tradeForm.priceBoxStringValue);

	const freeCollateral = NumLib.formatBn.fromQuote(
		currentAccount?.marginInfo?.freeCollateral ?? new BN(0)
	);

	const currentPositionDirection = currentPosition
		? currentPosition.netBaseBalance.isNeg()
			? 'short'
			: 'long'
		: // for logical purposes position is "long" if position is zero
		  'long';

	const minTradeSizeForMarket =
		BigNum.from(
			spotMarketAccount?.minOrderSize,
			spotMarketAccount?.decimals
		)?.toNum() ?? marketSizeIncs[selectedMarket.baseAssetSymbol()];

	// Price Box Heading
	const priceBoxHeading = getPriceBoxHeading(currentOrderType);

	// Price Box Enabled
	const priceBoxEnabled = (() => {
		switch (currentOrderType) {
			case 'market':
				return false;
			case 'oracle':
				return false;
			default:
				return true;
		}
	})();

	// Show Slippage Tolerance
	const showSlippageTolerance = (() => {
		switch (currentOrderType) {
			case 'market':
				return true;
			default:
				return false;
		}
	})();

	// Show Secondary Price Box
	const showSecondaryPriceBox =
		getShouldShowSecondaryPriceBox(currentOrderType);

	// Show Reduce Only
	const showReduceOnly = (() => {
		const selectedDirection = tradeForm.side == 'buy' ? 'long' : 'short';
		switch (currentOrderType) {
			case 'market':
				return (
					userHasPositionInMarket &&
					currentPositionDirection != selectedDirection
				);
			default:
				return true;
		}
	})();

	// Show Post Only
	const showPostOnly = useMemo(
		() => currentOrderType === 'limit' || currentOrderType === 'oracleLimit',
		[currentOrderType]
	);

	// Show Leverage Slider
	const showLeverageSlider = useMemo(() => {
		switch (currentOrderType) {
			case 'stopMarket':
			case 'takeProfitMarket':
			case 'stopLimit':
			case 'takeProfitLimit':
				return false;
			case 'oracleLimit':
				return false;
			default:
				if (tradeForm.reduceOnly) return false;
				return true;
		}
	}, [currentOrderType, tradeForm.reduceOnly]);

	const showMaxButton = (() => {
		let shouldShowMaxButtonValue = true;
		const tradeFormDirection = tradeForm.side === 'buy' ? 'long' : 'short';

		if (tradeForm.reduceOnly) {
			// If no position or if same side as position, hide max button
			if (
				!userHasPositionInMarket ||
				currentPositionDirection === tradeFormDirection
			) {
				shouldShowMaxButtonValue = false;
			}
		}

		return shouldShowMaxButtonValue;
	})();

	// Show Reduce Position Slider
	const showReducePositionSlider = (() => {
		if (!tradeForm.reduceOnly) return false;

		const tradeFormDirection = tradeForm.side === 'buy' ? 'long' : 'short';

		switch (currentOrderType) {
			case 'market':
				return userHasPositionInMarket;
			default:
				return (
					userHasPositionInMarket &&
					tradeFormDirection !== currentPositionDirection
				);
		}
	})();

	// When order type changes, default reduce-only to true for limit orders
	useEffect(() => {
		if (!userHasPositionInMarket) {
			// no position, so don't put set to reduce only regardless
			setReduceOnly(false);
			setState((s) => {
				s.tradeForm.immediateOrCancel = false;
			});
			return;
		}

		if (
			['market', 'takeProfitMarket', 'stopMarket'].includes(currentOrderType)
		) {
			setState((s) => {
				s.tradeForm.postOnly = false;
			});
		}

		if (currentOrderType !== 'limit' && currentOrderType !== 'market') {
			setReduceOnly(true);

			// Switch trade form to avoid bad reduce-only case
			if (currentPositionDirection) {
				if (currentPositionDirection === 'long' && tradeForm.side === 'buy') {
					setState((s) => {
						s.tradeForm.side = 'sell';
					});
				} else if (
					currentPositionDirection === 'short' &&
					tradeForm.side === 'sell'
				) {
					setState((s) => {
						s.tradeForm.side = 'buy';
					});
				}
			}
		} else {
			setState((s) => {
				s.tradeForm.immediateOrCancel = false;
			});
		}
	}, [currentOrderType, currentPositionDirection]);

	// Update main price box when changing markets for a non-Market Order
	useEffect(() => {
		if (!driftClientIsReady) return;

		if (currentOrderType === 'oracleLimit') {
			setState((s) => {
				s.tradeForm.baseSizeStringValue = '';
			});
		}

		if (currentOrderType !== 'market') {
			if (
				(currentMarketPriceBigNum && !currentMarketPriceBigNum.eqZero()) ||
				savedLimitPrice
			) {
				marketSwitchPriceBoxUpdate(
					currentMarketPriceBigNum,
					currentOrderType,
					tradeForm.side === 'buy' ? 'long' : 'short',
					currentPositionDirection,
					slippageTolerance,
					setPriceBoxValue,
					setSecondaryPriceBoxValue,
					tickSize ? BigNum.from(tickSize, PRICE_PRECISION_EXP) : undefined,
					savedLimitPrice
				);
			} else {
				setPriceBoxValue('');
				setSecondaryPriceBoxValue('');
			}
		}
	}, [
		selectedMarketIndex,
		driftClientIsReady,
		currentOrderType,
		currentPositionDirection,
		tradeForm.side,
		savedLimitPrice?.toNum(),
		currentMarketPriceBigNum?.eqZero(),
	]);

	// Maintain max allowed quote and base state
	useEffect(() => {
		if (!userAccountIsReady || !driftClientIsReady || !currentAccount) {
			setMaxQuoteAmountAvailable(0);
			setMaxBaseAmountAvailable(0);
			return;
		}

		const maxQuoteAllowable = currentAccount.client.getMaxTradeSizeUSDCForSpot(
			selectedMarketIndex,
			tradeForm.side === 'buy'
				? PositionDirection.LONG
				: PositionDirection.SHORT
		);

		setMaxQuoteAmountAvailable(
			BigNum.from(maxQuoteAllowable, QUOTE_PRECISION_EXP).toNum()
		);

		if (oraclePrice && oraclePrice.gt(ZERO)) {
			setMaxBaseAmountAvailable(
				BigNum.from(maxQuoteAllowable, QUOTE_PRECISION_EXP)
					.scale(100_000, oraclePrice.toNum() * 100_000)
					.toNum()
			);
		}

		return;
	}, [
		freeCollateral,
		maxLeverageAvailable,
		selectedMarketIndex,
		tradeForm.side,
		marketPrice,
		userAccountIsReady,
		driftClientIsReady,
		connected,
		oraclePrice?.toString(),
	]);

	// Maintain max allowed leverage state
	useEffect(() => {
		if (!userAccountIsReady || !driftClientIsReady || !connected) {
			setMaxLeverageAvailable(Env.defaultMaxMarketLeverage);
			return;
		}

		if (freeCollateral === 0) return;

		try {
			const newMaxLeverage = BigNum.from(
				currentAccount.client.getMaxLeverageForSpot(
					selectedMarketIndex,
					tradeForm.side === 'buy'
						? PositionDirection.LONG
						: PositionDirection.SHORT
				),
				FOUR
			).toNum();

			setMaxLeverageAvailable(newMaxLeverage);
		} catch (e) {
			// little race condition here after wallet disconnect so wrap in try/catch
		}

		return;
	}, [
		selectedMarketIndex,
		currentAccount?.userKey,
		tradeForm.side,
		maxQuoteAvailable,
		freeCollateral,
		userAccountIsReady,
		driftClientIsReady,
		connected,
	]);

	/* Keep tradeform slippage tolerance synced if updated from settings */
	useEffect(() => {
		setState((s) => {
			s.tradeForm.slippageTolerance =
				globalSlippageTolerance === 'inf'
					? undefined
					: Number(globalSlippageTolerance);
			s.tradeForm.allowInfSlippage = globalAllowInfSlippage;
		});
	}, [globalSlippageTolerance, globalAllowInfSlippage]);

	// can't be reduce only if we're not showing it
	useEffect(() => {
		if (!showReduceOnly) {
			setReduceOnly(false);
		}
	}, [showReduceOnly, tradeForm.reduceOnly]);

	const usingSpotReduceOnlyOverride = useRef(false);

	useEffect(() => {
		if (!userHasMarginEnabled && tradeForm.side === 'sell') {
			usingSpotReduceOnlyOverride.current = true;
			setReduceOnly(true);
		} else if (usingSpotReduceOnlyOverride.current) {
			usingSpotReduceOnlyOverride.current = false;
			setReduceOnly(false);
		}
	}, [userHasMarginEnabled, tradeForm.side]);

	useEffect(() => {
		if (
			tradeForm.orderType == 'market' &&
			Number(tradeForm.quoteSizeStringValue) > maxQuoteAvailable
		) {
			setState((s) => {
				s.tradeForm.leadSide = 'quote';
				s.tradeForm.quoteSizeStringValue = String(maxQuoteAvailable);
			});
		}
	}, [
		tradeForm.side,
		maxQuoteAvailable,
		tradeForm.quoteSizeStringValue,
		tradeForm.baseSizeStringValue,
		tradeForm.orderType,
	]);

	const openAddAccountModal = () => {
		setState((s) => {
			s.modals.showNewSubaccountModal = true;
		});
	};

	const setReduceOnly = (newVal: boolean) => {
		setState((s) => {
			s.tradeForm.reduceOnly = newVal;
		});
	};

	const setPriceBoxValue = (newValue: string) => {
		setState((s) => {
			s.tradeForm.priceBoxStringValue = newValue;
		});
	};

	const setSecondaryPriceBoxValue = (newValue: string) =>
		setState((s) => {
			s.tradeForm.secondaryPriceBoxStringValue = newValue;
		});

	const {
		messageType,
		shouldShowMessage,
		message,
		skipMessageInConfirmationModal,
		messageLink,
		messageLinkDescription,
		messageTitle,
		disableTradeButton,
		messageId,
	} = getMessageInfoForTradeformState(
		true,
		currentOrderType,
		maxQuoteAvailable,
		connected,
		accountExists,
		tradeForm,
		priceBoxValue,
		currentMarketPrice,
		userHasPositionInMarket,
		isGeoblocked,
		minTradeSizeForMarket,
		selectedMarket.baseAssetSymbol(),
		BigNum.from(entryPriceBn, PRICE_PRECISION_EXP),
		MarketType.SPOT,
		openAddAccountModal,
		false,
		oraclePrice ? oraclePrice : undefined,
		currentPositionDirection,
		currentPosition.netBaseBalance.abs(),
		basePrecisionToUse,
		userHasLpShares,
		currentAccount?.name
	);

	const tradeButtonIsDisabled =
		UI_UTILS.valueIsBelowStepSize(
			tradeForm.baseSizeStringValue,
			tradeForm.stepSize
		) ||
		disableTradeButton ||
		!tradeForm.baseSizeStringValue ||
		!connected ||
		!quoteSize;

	return {
		orderType: currentOrderType,
		priceBoxHeading,
		priceBoxEnabled,
		showSlippageTolerance,
		shouldShowMessage,
		messageType,
		message,
		skipMessageInConfirmationModal,
		showSecondaryPriceBox,
		showReduceOnly,
		showPostOnly,
		showLeverageSlider,
		tradeButtonIsDisabled,
		currentPosition: undefined,
		maxQuoteAvailable,
		maxBaseAvailable,
		currentMarketPrice,
		showReducePositionSlider,
		messageLink,
		messageLinkDescription,
		messageTitle,
		maxLeverageAvailable,
		messageId,
		showMaxButton,
		showPriceEstimateOracleDivergenceWarning:
			showPriceEstimateOracleDivergenceWarning && currentOrderType === 'market',
	};
};
